'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Search, Sparkles, MapPin, TrendingUp, Shield, ChevronDown } from 'lucide-react';

const SEARCH_TYPES = ['Satılık', 'Kiralık', 'Arsa', 'İş Yeri'];

const POPULAR_SEARCHES = [
  'İstanbul Kadıköy',
  'Muğla Bodrum',
  'Antalya Konyaaltı',
  'İzmir Çeşme',
  'Ankara Çankaya',
  'Bursa Nilüfer',
];

const QUICK_STATS = [
  { value: '2.4M+', label: 'Aktif İlan', icon: <TrendingUp size={16} /> },
  { value: '81', label: 'İl', icon: <MapPin size={16} /> },
  { value: '850K+', label: 'Mutlu Kullanıcı', icon: <Shield size={16} /> },
];

export default function HeroSection() {
  const [activeType, setActiveType] = useState('Satılık');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredSuggestions = POPULAR_SEARCHES?.filter(s =>
    searchQuery?.length === 0 || s?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <section className="relative min-h-[92vh] flex flex-col items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 gradient-hero" />
      <div className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      {/* Floating property bubbles */}
      <div className="absolute top-24 right-10 hidden xl:block animate-fade-in">
        <div className="glass-card rounded-2xl p-4 w-52 shadow-xl">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <TrendingUp size={14} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">İstanbul Ortalama</p>
              <p className="text-sm font-bold text-foreground tabular-nums">₺87.500/m²</p>
            </div>
          </div>
          <div className="text-xs text-success flex items-center gap-1">
            <TrendingUp size={11} />
            <span>Son 3 ayda +%12.4 artış</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-32 left-10 hidden xl:block animate-fade-in" style={{ animationDelay: '0.3s' }}>
        <div className="glass-card rounded-2xl p-4 w-48 shadow-xl">
          <p className="text-xs text-muted-foreground mb-1">Yeni İlan</p>
          <p className="text-sm font-bold text-foreground">Kadıköy 3+1 Daire</p>
          <p className="text-base font-extrabold text-primary tabular-nums mt-1">₺9.750.000</p>
          <div className="flex items-center gap-1 mt-2">
            <span className="badge-new">Yeni İlan</span>
          </div>
        </div>
      </div>

      <div className="absolute top-40 left-14 hidden xl:block animate-fade-in" style={{ animationDelay: '0.6s' }}>
        <div className="glass-card rounded-2xl p-3 shadow-xl">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-success flex items-center justify-center">
              <Shield size={12} className="text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground">Doğrulanmış İlan</p>
              <p className="text-[10px] text-muted-foreground">Güvenli alışveriş</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pt-24 pb-16">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-semibold mb-6">
            <Sparkles size={14} className="text-accent" />
            Türkiye&apos;nin AI Destekli Emlak Platformu
          </div>

          {/* Headline */}
          <h1 className="text-hero-xl font-extrabold text-white mb-4 leading-tight">
            Hayalindeki evi bul.
          </h1>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto leading-relaxed">
            AI destekli arama, 3D tur ve yatırım analizi ile Türkiye&apos;nin her köşesindeki ilanları keşfet.
          </p>

          {/* Search Box */}
          <div className="bg-white rounded-2xl shadow-xl overflow-visible">
            {/* Type Tabs */}
            <div className="flex border-b border-border">
              {SEARCH_TYPES?.map((type) => (
                <button
                  key={`search-type-${type}`}
                  onClick={() => setActiveType(type)}
                  className={`flex-1 py-3.5 text-sm font-semibold transition-all duration-150 first:rounded-tl-2xl last:rounded-tr-2xl ${activeType === type ? 'text-primary border-b-2 border-primary bg-primary-light' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="p-4">
              <div className="relative">
                <div className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e?.target?.value); setShowSuggestions(true); }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Nerede yaşamak istiyorsun? (İstanbul, Kadıköy, Bodrum...)"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-border text-sm font-medium focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-foreground placeholder:text-muted-foreground"
                    />

                    {/* Suggestions Dropdown */}
                    {showSuggestions && filteredSuggestions?.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-fade-in">
                        <div className="p-2">
                          <p className="text-xs text-muted-foreground px-3 py-1.5 font-semibold uppercase tracking-wide">Popüler Aramalar</p>
                          {filteredSuggestions?.map((suggestion) => (
                            <button
                              key={`suggestion-${suggestion}`}
                              onMouseDown={() => { setSearchQuery(suggestion); setShowSuggestions(false); }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-light hover:text-primary text-sm font-medium text-foreground text-left transition-colors"
                            >
                              <MapPin size={14} className="text-primary flex-shrink-0" />
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI Search Button */}
                  <Link
                    href={`/property-search?q=${encodeURIComponent(searchQuery)}&tip=${activeType?.toLowerCase()}`}
                    className="btn-primary px-6 py-3.5 text-base whitespace-nowrap"
                  >
                    <Search size={18} />
                    Ara
                  </Link>
                </div>

                {/* AI Search */}
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles size={14} className="text-accent" />
                    <span className="text-xs">AI ile ara:</span>
                  </div>
                  <Link
                    href="/property-search?ai=true&q=İstanbul+Kadıköy+3%2B1+10+milyon+TL+altı"
                    className="text-xs text-primary font-semibold hover:underline"
                  >
                    &ldquo;Kadıköy&apos;de 3+1, 10M TL altı&rdquo;
                  </Link>
                  <span className="text-muted-foreground text-xs">·</span>
                  <Link
                    href="/property-search?ai=true&q=Bodrum+deniz+manzaralı+villa"
                    className="text-xs text-primary font-semibold hover:underline"
                  >
                    &ldquo;Bodrum&apos;da deniz manzaralı villa&rdquo;
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Secondary CTA */}
          <div className="mt-6 flex items-center justify-center gap-4">
            <Link href="/property-search?view=map" className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors">
              <MapPin size={16} />
              Haritada Keşfet
            </Link>
            <span className="text-white/30">·</span>
            <Link href="/property-search?yatirim=true" className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors">
              <TrendingUp size={16} />
              Yatırım Fırsatları
            </Link>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-14 flex items-center justify-center gap-8 md:gap-16">
          {QUICK_STATS?.map((stat) => (
            <div key={`hero-stat-${stat?.label}`} className="text-center">
              <div className="flex items-center justify-center gap-1.5 text-white/60 text-xs font-medium mb-1">
                {stat?.icon}
                {stat?.label}
              </div>
              <p className="text-2xl font-extrabold text-white tabular-nums">{stat?.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={24} className="text-white/40" />
      </div>
    </section>
  );
}